import React, { useCallback, useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Switch } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { createStandaloneToast } from '@chakra-ui/react';
import {
  scheduledConversationsAtom,
  scheduledConversationsLoadingAtom,
  scheduledConversationsErrorAtom,
  updateScheduledConversationAtom,
  fetchScheduledConversationsAtom,
  triggerScheduledConversationAtom,
  ScheduledConversation
} from '../../../../atoms/v3/scheduledConversationAtoms';
import { SettingsItem } from './SettingsItem';
import { ChannelSelector } from '../../../../components/v3/ChannelSelector';
import { ReminderMethodType, createDefaultReminderMethods, formatReminderMethods } from '../../../../types/notificationMapping';

// 创建独立的 toast 实例
const { toast } = createStandaloneToast();

/**
 * 定时对话设置组件
 */
export const ScheduledConversationsSection: React.FC = () => {
  const { t } = useTranslation();
  const conversations = useAtomValue(scheduledConversationsAtom);
  const isLoading = useAtomValue(scheduledConversationsLoadingAtom);
  const error = useAtomValue(scheduledConversationsErrorAtom);
  const [, updateConversation] = useAtom(updateScheduledConversationAtom);
  const [, fetchConversations] = useAtom(fetchScheduledConversationsAtom);
  const [, triggerConversation] = useAtom(triggerScheduledConversationAtom);
  const [triggeringId, setTriggeringId] = React.useState<string | null>(null);
  const [cooldowns, setCooldowns] = React.useState<Record<string, number>>({});

  // 推送渠道选择器状态
  const [channelSelectorOpen, setChannelSelectorOpen] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  // // 初始化时获取数据
  // useEffect(() => {
  //   fetchConversations().catch(console.error);
  // }, [fetchConversations]);

  // 倒计时效果
  useEffect(() => {
    const interval = setInterval(() => {
      setCooldowns(prev => {
        const updated = { ...prev };
        let hasChanges = false;

        Object.keys(updated).forEach(id => {
          if (updated[id] > 0) {
            updated[id] -= 1;
            hasChanges = true;
          } else {
            delete updated[id];
            hasChanges = true;
          }
        });

        return hasChanges ? updated : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // 处理开关切换
  const handleToggle = useCallback(async (id: string, enabled: boolean) => {
    try {
      await updateConversation({ id, enabled });
    } catch (error) {
      console.error('Failed to update scheduled conversation config:', error);
      // 可以在这里添加错误提示
    }
  }, [updateConversation]);

  // 处理推送渠道选择
  const handleChannelSelect = useCallback((conversationId: string) => {
    setSelectedConversationId(conversationId);
    setChannelSelectorOpen(true);
  }, []);

  // 处理推送渠道确认
  const handleChannelConfirm = useCallback(async (channels: ReminderMethodType[]) => {
    if (!selectedConversationId) return;

    try {
      await updateConversation({
        id: selectedConversationId,
        channels
      });

      toast({
        title: '推送渠道更新成功',
        description: `已设置为：${formatReminderMethods(channels)}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } catch (error) {
      console.error('更新推送渠道失败:', error);
      toast({
        title: '推送渠道更新失败',
        description: error instanceof Error ? error.message : '未知错误',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    }
  }, [selectedConversationId, updateConversation]);

  // 处理刷新
  const handleRefresh = useCallback(async () => {
    try {
      await fetchConversations();
    } catch (error) {
      console.error('刷新定时对话配置失败:', error);
    }
  }, [fetchConversations]);

  // 处理手动触发
  const handleTrigger = useCallback(async (id: string) => {
    if (triggeringId || cooldowns[id]) return; // 防止重复触发或在冷却期间触发

    setTriggeringId(id);
    try {
      await triggerConversation(id);

      // 显示成功 Toast
      toast({
        title: '已加入处理队列',
        description: '你将会在几分钟后拿到报告~',
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top'
      });

      // 设置 180 秒冷却时间
      setCooldowns(prev => ({ ...prev, [id]: 180 }));

    } catch (error) {
      console.error('触发定时对话失败:', error);

      // 显示错误 Toast
      toast({
        title: '触发失败',
        description: error instanceof Error ? error.message : '未知错误',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setTriggeringId(null);
    }
  }, [triggerConversation, triggeringId, cooldowns]);

  // 格式化倒计时显示
  const formatCooldown = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // 如果正在加载，显示加载状态
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg animate-pulse">
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/3"></div>
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-12"></div>
        </div>
        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg animate-pulse">
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/3"></div>
          <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded w-12"></div>
        </div>
      </div>
    );
  }

  // 如果有错误，显示错误状态
  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-red-600 dark:text-red-400 text-sm">
              {t('v3/me:scheduledConversations.loadError', '加载定时对话配置失败')}
            </p>
            <p className="text-red-500 dark:text-red-300 text-xs mt-1">{error}</p>
          </div>
          <button
            onClick={handleRefresh}
            className="px-3 py-1 text-xs bg-red-100 dark:bg-red-800 text-red-600 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
          >
            {t('common:actions.retry', '重试')}
          </button>
        </div>
      </div>
    );
  }

  // 如果没有配置，显示空状态
  if (conversations.length === 0) {
    return (
      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          {t('v3/me:scheduledConversations.empty', '暂无定时对话配置')}
        </p>
        <button
          onClick={handleRefresh}
          className="mt-2 px-3 py-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        >
          {t('common:actions.refresh', '刷新')}
        </button>
      </div>
    );
  }

  return (
    <>
      {conversations.map((conversation) => (
        <SettingsItem
          key={conversation.id}
          icon={getConversationIcon(conversation.templateKey)}
          iconBgColor={getConversationIconBgColor(conversation.templateKey)}
          title={
            <div className="flex items-center gap-2">
              <span>{conversation.name}</span>
              {conversation.isSystemInstance && (
                <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">
                  {t('v3/me:scheduledConversations.systemLabel', '系统')}
                </span>
              )}
            </div>
          }
          description={getConversationDescription(conversation.templateKey, conversation.isSystemInstance)}
          showChevron={false}
          rightElement={
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleTrigger(conversation.id)}
                disabled={triggeringId === conversation.id || !!cooldowns[conversation.id]}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  triggeringId === conversation.id || cooldowns[conversation.id]
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-800/50'
                }`}
                title={
                  cooldowns[conversation.id]
                    ? `冷却中，剩余 ${formatCooldown(cooldowns[conversation.id])}`
                    : '立刻触发一次'
                }
              >
                {triggeringId === conversation.id
                  ? '触发中...'
                  : cooldowns[conversation.id]
                    ? formatCooldown(cooldowns[conversation.id])
                    : '立刻触发'
                }
              </button>
              <button
                onClick={() => handleChannelSelect(conversation.id)}
                className="px-2 py-1 text-xs rounded transition-colors bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/50"
                title={`当前推送渠道：${formatReminderMethods(conversation.channels || createDefaultReminderMethods())}`}
              >
                {t('v3/me:scheduledConversations.pushChannel', '推送渠道')}
              </button>
              <Switch
                isChecked={conversation.enabled}
                onChange={(e) => handleToggle(conversation.id, e.target.checked)}
                colorScheme="blue"
                size="sm"
              />
            </div>
          }
        />
      ))}

      {/* 推送渠道选择器 */}
      <ChannelSelector
        isOpen={channelSelectorOpen}
        onClose={() => {
          setChannelSelectorOpen(false);
          setSelectedConversationId(null);
        }}
        selectedChannels={
          selectedConversationId
            ? conversations.find(c => c.id === selectedConversationId)?.channels || createDefaultReminderMethods()
            : createDefaultReminderMethods()
        }
        onConfirm={handleChannelConfirm}
        title={t('v3/me:notifications.selectChannels', '选择推送渠道')}
        showUserPreferenceHint={true}
      />
    </>
  );
};

/**
 * 根据模板键获取描述文本
 */
function getConversationDescription(templateKey: string, isSystemInstance: boolean): string {
  let baseDescription = '';
  switch (templateKey) {
    case 'daily_morning':
      baseDescription = '每天早上10:00自动发送，总结昨日成果并规划今日任务';
      break;
    case 'daily_evening':
      baseDescription = '每天晚上20:00自动发送，回顾今日效率并总结未完成任务';
      break;
    default:
      baseDescription = '自动定时发送的对话消息';
  }

  return baseDescription;
}

/**
 * 根据模板键获取图标
 */
function getConversationIcon(templateKey: string): React.ReactNode {
  switch (templateKey) {
    case 'daily_morning':
      return (
        <svg className="w-5 h-5 text-ios-orange dark:text-ios-dark-orange" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
    case 'daily_evening':
      return (
        <svg className="w-5 h-5 text-ios-indigo dark:text-ios-dark-indigo" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
  }
}

/**
 * 根据模板键获取图标背景色
 */
function getConversationIconBgColor(templateKey: string): string {
  switch (templateKey) {
    case 'daily_morning':
      return 'bg-ios-orange/10 dark:bg-ios-dark-orange/20';
    case 'daily_evening':
      return 'bg-ios-indigo/10 dark:bg-ios-dark-indigo/20';
    default:
      return 'bg-ios-blue/10 dark:bg-ios-dark-blue/20';
  }
}

export default ScheduledConversationsSection;
